import React, { useEffect, useMemo, useRef, useState } from "react";
import { Modal, Spinner } from "reactstrap";
import FeatherIcon from "feather-icons-react";
import { toast } from "react-toastify";
import { getPadAttachmentUrl } from "./api";
import { downloadFile, formatBytes, padFileName, printFile } from "./fileActions";
import { Previewer } from "../../../../../../components/DocumentPreviewer/Previewer";

const C = {
  primaryMd: "#1A6FB5",
  border: "#E4E9F2",
  inputBorder: "#D6DEEA",
  muted: "#64748B",
  text: "#0F1F35",
  soft: "#F7F9FC",
};

export const MAX_ATTACHMENTS = 10;
export const MAX_ATTACHMENT_BYTES = 10 * 1024 * 1024;

const iconFor = (filename, mime) => {
  const f = `${filename || ""} ${mime || ""}`.toLowerCase();
  if (f.includes("pdf")) return "file-text";
  if (/(png|jpe?g|gif|webp|bmp|image)/.test(f)) return "image";
  if (/(xls|sheet|csv)/.test(f)) return "grid";
  if (/(doc|word)/.test(f)) return "file-text";
  if (/(msg|eml|mail)/.test(f)) return "mail";
  return "paperclip";
};

/**
 * Compact attachments panel. It sits beside the category list in the PAD modal, so it lists
 * files in slim rows and pushes everything else (preview, download, print) into a second
 * modal opened from the eye icon. With `fill` it stretches to its column's height and, while
 * empty, the whole panel is the drop zone.
 *
 * Fully staged, like the rest of the form: choosing a file adds it to a pending list, removing
 * a saved one marks it, and nothing reaches storage until the entry is saved. That is what lets
 * paperwork ride along on a brand-new entry, and it means Cancel leaves no orphaned uploads.
 */
const PadAttachments = ({
  entryId,
  saved = [],
  staged = [],
  removedIds = [],
  onStage,
  onUnstage,
  onToggleRemove,
  // Context for the accounting-friendly file name a download or print carries.
  unitNo = "",
  propertyName = "",
  entryCategoryLabel = "",
  entryDate = "",
  canWrite = true,
  readOnly = false,
  busy = false,
  fill = false,
}) => {
  const [dragOver, setDragOver] = useState(false);
  // {name, mime, url, loading}: the second modal. Saved files fetch a signed link first.
  const [preview, setPreview] = useState(null);
  const inputRef = useRef(null);

  const savedLive = useMemo(() => (saved || []).filter((a) => a && !a.removed), [saved]);
  const removed = useMemo(() => new Set(removedIds || []), [removedIds]);
  const editable = canWrite && !readOnly;

  // Local URLs so a just-picked file can be previewed before it has ever been uploaded.
  const stagedUrls = useMemo(() => (staged || []).map((f) => URL.createObjectURL(f)), [staged]);
  useEffect(() => () => stagedUrls.forEach((u) => URL.revokeObjectURL(u)), [stagedUrls]);

  const keptCount = savedLive.filter((a) => !removed.has(a.attachment_id)).length + (staged || []).length;
  const isEmpty = savedLive.length === 0 && (staged || []).length === 0;

  // Every file leaves here under the name accounting files it by, not its upload name.
  const accountingName = (originalName) =>
    padFileName({ unitNo, propertyName, categoryLabel: entryCategoryLabel, date: entryDate, originalName });

  const browse = () => inputRef.current?.click();

  const stage = (files) => {
    const list = Array.from(files || []);
    if (!list.length) return;
    if (keptCount + list.length > MAX_ATTACHMENTS) {
      toast.error(`An entry can hold at most ${MAX_ATTACHMENTS} attachments.`);
      return;
    }
    const tooBig = list.find((f) => f.size > MAX_ATTACHMENT_BYTES);
    if (tooBig) {
      toast.error(`"${tooBig.name}" is larger than 10 MB.`);
      return;
    }
    onStage && onStage(list);
    if (inputRef.current) inputRef.current.value = "";
  };

  const previewSaved = async (att) => {
    setPreview({ name: att.filename, mime: att.mime, url: "", loading: true });
    try {
      const { data } = await getPadAttachmentUrl(entryId, att.attachment_id);
      const url = data?.data?.url;
      if (!url) throw new Error("No link came back for this file");
      setPreview({ name: att.filename, mime: att.mime, url, loading: false });
    } catch (e) {
      toast.error(e?.response?.data?.message || e.message || "Could not open the file");
      setPreview(null);
    }
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    if (editable && !busy) stage(e.dataTransfer.files);
  };

  const IconBtn = ({ icon, title, danger, onClick, disabled }) => (
    <button type="button" className={`pa-ib${danger ? " danger" : ""}`} title={title} disabled={disabled} onClick={onClick}>
      <FeatherIcon icon={icon} size={13} />
    </button>
  );

  return (
    <div className={`pa-wrap${fill ? " fill" : ""}`}>
      <style>{`
        .pa-wrap.fill { height: 100%; display: flex; flex-direction: column; }
        .pa-box { border: 1px solid ${C.inputBorder}; border-radius: 10px; background: #fff; padding: 6px;
          transition: border-color .12s, background .12s; }
        .pa-box.empty { border-style: dashed; background: ${C.soft}; }
        .pa-box.over { border-color: ${C.primaryMd}; background: #F4F9FF; }
        .pa-wrap.fill .pa-box { flex: 1; display: flex; flex-direction: column; min-height: 0; }
        .pa-list { max-height: 118px; overflow-y: auto; }
        .pa-wrap.fill .pa-list { flex: 1; max-height: none; min-height: 0; display: flex; flex-direction: column; }
        .pa-wrap.fill .pa-list > * { flex: none; }
        .pa-r { display: flex; align-items: center; gap: 7px; padding: 4px 6px; border-radius: 7px; min-height: 30px; }
        .pa-r:hover { background: ${C.soft}; }
        .pa-r.off .pa-n { text-decoration: line-through; opacity: .55; }
        .pa-r .ic { flex: none; color: ${C.muted}; display: inline-flex; }
        .pa-r.new .ic { color: ${C.primaryMd}; }
        .pa-n { flex: 1; min-width: 0; font-size: 12.5px; font-weight: 600; color: ${C.text};
          overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
        .pa-sz { font-size: 11px; color: ${C.muted}; flex: none; }
        .pa-ib { border: none; background: none; border-radius: 6px; width: 26px; height: 26px; flex: none;
          display: inline-flex; align-items: center; justify-content: center; color: ${C.muted}; cursor: pointer; }
        .pa-ib:hover:not(:disabled) { background: #E9F1FA; color: ${C.primaryMd}; }
        .pa-ib.danger:hover:not(:disabled) { background: #FDECEC; color: #C62828; }
        .pa-ib:disabled { opacity: .4; cursor: not-allowed; }
        .pa-add { display: flex; align-items: center; justify-content: center; gap: 6px; width: 100%;
          border: 1.5px dashed ${C.inputBorder}; border-radius: 8px; background: ${C.soft}; color: ${C.primaryMd};
          font-size: 12.5px; font-weight: 600; padding: 7px 8px; cursor: pointer; font-family: inherit;
          transition: border-color .12s, background .12s; }
        .pa-add:hover:not(:disabled) { border-color: ${C.primaryMd}; background: #F4F9FF; }
        .pa-add:disabled { opacity: .5; cursor: not-allowed; }
        .pa-wrap.fill .pa-list + .pa-add { margin-top: 6px; }
        /* Empty state: the panel itself is the drop zone, so there is one box, not a box in a box. */
        .pa-empty { display: flex; flex-direction: column; align-items: center; justify-content: center; text-align: center;
          gap: 3px; width: 100%; padding: 18px 12px; border: none; background: none; cursor: pointer; font-family: inherit;
          color: ${C.text}; border-radius: 7px; }
        .pa-wrap.fill .pa-list > .pa-empty { flex: 1; }
        .pa-empty:disabled { cursor: not-allowed; opacity: .6; }
        .pa-empty .ic { width: 34px; height: 34px; border-radius: 10px; background: #fff; border: 1px solid ${C.border};
          color: ${C.primaryMd}; display: inline-flex; align-items: center; justify-content: center; margin-bottom: 5px; }
        .pa-empty .t { font-size: 12.5px; font-weight: 700; color: ${C.text}; }
        .pa-empty .t u { text-decoration: underline; text-underline-offset: 2px; color: ${C.primaryMd}; }
        .pa-none { font-size: 11.5px; color: ${C.muted}; padding: 4px 6px 8px; }
        .pa-pv-body { background: #40495A; display: flex; align-items: center; justify-content: center; min-height: 220px; }
        .pa-pv-body img { max-width: 100%; max-height: 74vh; display: block; }
        .pa-pv-body iframe { width: 100%; height: 74vh; border: none; background: #fff; }
        .pa-pv-none { color: #fff; text-align: center; padding: 40px 20px; font-size: 13.5px; }
      `}</style>

      <div
        className={`pa-box${isEmpty && editable ? " empty" : ""}${dragOver ? " over" : ""}`}
        onDragOver={(e) => {
          e.preventDefault();
          setDragOver(true);
        }}
        onDragLeave={() => setDragOver(false)}
        onDrop={onDrop}
      >
        <div className="pa-list">
          {isEmpty && editable ? (
            <button type="button" className="pa-empty" disabled={busy} onClick={browse}>
              <span className="ic"><FeatherIcon icon="upload-cloud" size={17} /></span>
              <span className="t">Drop files here, or <u>browse</u></span>
            </button>
          ) : null}
          {isEmpty && !editable ? <div className="pa-none">No documents.</div> : null}

          {/* Already saved on the entry. */}
          {savedLive.map((a) => {
            const isRemoved = removed.has(a.attachment_id);
            return (
              <div key={a.attachment_id} className={`pa-r${isRemoved ? " off" : ""}`}>
                <span className="ic"><FeatherIcon icon={iconFor(a.filename, a.mime)} size={14} /></span>
                <span className="pa-n" title={`${a.filename}${a.uploaded_by?.name ? ` · ${a.uploaded_by.name}` : ""}`}>{a.filename}</span>
                <span className="pa-sz">{isRemoved ? "removes on save" : formatBytes(a.size)}</span>
                <IconBtn icon="eye" title="Preview" disabled={busy} onClick={() => previewSaved(a)} />
                {editable ? (
                  <IconBtn
                    icon={isRemoved ? "rotate-ccw" : "trash-2"}
                    title={isRemoved ? "Keep this file" : "Remove"}
                    danger={!isRemoved}
                    disabled={busy}
                    onClick={() => onToggleRemove && onToggleRemove(a.attachment_id)}
                  />
                ) : null}
              </div>
            );
          })}

          {/* Picked but not uploaded yet; they go up when the entry is saved. */}
          {(staged || []).map((f, i) => (
            <div className="pa-r new" key={`${f.name}-${i}`}>
              <span className="ic"><FeatherIcon icon={iconFor(f.name, f.type)} size={14} /></span>
              <span className="pa-n" title={`${f.name} · uploads when you save`}>{f.name}</span>
              <span className="pa-sz">{formatBytes(f.size)}</span>
              <IconBtn icon="eye" title="Preview" disabled={busy} onClick={() => setPreview({ name: f.name, mime: f.type, url: stagedUrls[i], loading: false })} />
              {editable ? <IconBtn icon="x" title="Remove" danger disabled={busy} onClick={() => onUnstage && onUnstage(i)} /> : null}
            </div>
          ))}
        </div>

        {editable ? (
          <>
            {!isEmpty ? (
              <button type="button" className="pa-add" disabled={busy} onClick={browse}>
                <FeatherIcon icon="paperclip" size={13} /> Add another file
              </button>
            ) : null}
            <input ref={inputRef} type="file" multiple style={{ display: "none" }} onChange={(e) => stage(e.target.files)} />
          </>
        ) : null}
      </div>

      {/* The second modal: preview, and from here download or print. */}
      <Modal isOpen={!!preview} toggle={() => setPreview(null)} size="lg" centered zIndex={1060}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "12px 16px", borderBottom: `1px solid ${C.border}` }}>
          <FeatherIcon icon={iconFor(preview?.name, preview?.mime)} size={16} color={C.muted} style={{ flex: "none" }} />
          <div style={{ flex: 1, minWidth: 0, fontSize: 14, fontWeight: 700, color: C.text, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {preview?.name || "Preview"}
          </div>
          <button type="button" className="pa-ib" onClick={() => setPreview(null)} aria-label="Close preview">
            <FeatherIcon icon="x" size={16} />
          </button>
        </div>
        <div className="pa-pv-body">
          {!preview || preview.loading ? (
            <Spinner color="light" />
          ) : !preview.url ? (
            <div className="pa-pv-none">The file link could not be created.</div>
          ) : (
            /* The shared previewer: pdf, images, Office documents, video — one renderer for
               the whole app. It takes the file type from the FILENAME's extension (the
               signed URL's path carries query noise). */
            <Previewer signedUrl={preview.url} url={preview.name} height="74vh" />
          )}
        </div>
        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, padding: "10px 16px", borderTop: `1px solid ${C.border}` }}>
          <button
            type="button"
            className="pa-add"
            style={{ width: "auto", borderStyle: "solid" }}
            disabled={!preview?.url}
            onClick={() => downloadFile(preview.url, accountingName(preview.name)).catch((e) => toast.error(e.message || "Download failed"))}
          >
            <FeatherIcon icon="download" size={13} /> Download
          </button>
          <button
            type="button"
            className="pa-add"
            style={{ width: "auto", borderStyle: "solid" }}
            disabled={!preview?.url}
            onClick={() => printFile(preview.url, accountingName(preview.name), preview.mime).catch((e) => toast.error(e.message || "Print failed"))}
          >
            <FeatherIcon icon="printer" size={13} /> Print
          </button>
        </div>
      </Modal>
    </div>
  );
};

export default PadAttachments;
